import {
  Flex,
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Button,
  useDisclosure,
  ButtonGroup,
  Alert,
  AlertTitle,
  AlertDescription,
  VStack,
  AlertIcon,
  Badge,
  useToast,
} from '@chakra-ui/react'
import * as React from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'

// Custom components
import Card from 'components/card/Card'
import { useGetUsers } from 'queries/user.query'
import { SkeletonTable } from 'components/skeleton/Skeleton'
import { AiOutlineDelete, AiOutlineUserAdd } from 'react-icons/ai'
import { CustomPagination } from './pagination'
import { EditUserModal } from './EditUserModal'
import { EditIcon } from '@chakra-ui/icons'
import { CreateUserModal } from './CreateUserModal'
import { DeleteUserModal } from './DeleteUserModal'
import { useDeleteUser } from 'queries/user.query'

const columnHelper = createColumnHelper()

export default function UserTable () {
  const [sorting, setSorting] = React.useState([])
  const textColor = useColorModeValue('secondaryGray.900', 'white')
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100')
  const TheadBackground = useColorModeValue('white', 'gray.800')
  const hoverBackgroundColor = useColorModeValue('gray.100', 'gray.700') // REMOVED: No multi-selection hover logic needed
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { mutate, isPending } = useDeleteUser()
  const [isEditOpen, setIsEditOpen] = React.useState(false)
  const [selectedUser, setSelectedUser] = React.useState(null)
  const toast = useToast()

  const {
    isOpen: isCreateOpen,
    onOpen: onOpenCreateUser,
    onClose: onCloseCreateUser,
  } = useDisclosure()

  const [selectedUserForDeletion, setSelectedUserForDeletion] =
    React.useState(null)

  const handleSingleDelete = id => {
    setSelectedUserForDeletion(id)
    onOpen()
  }

  const [pagination, setPagination] = React.useState({
    current: 1,
    pageSize: 10,
    searchQuery: null,
  })

  const { data, isLoading, isError, refetch } = useGetUsers(
    pagination.current,
    pagination.pageSize,
    pagination.searchQuery,
  )

  const rows = data?.data || []

  const onPageChange = page => {
    setPagination(prev => ({
      ...prev,
      current: page,
    }))
  }

  const onPageSizeChange = value => {
    setPagination(prev => ({
      ...prev,
      pageSize: value,
    }))
  }

  const handleEditOpen = user => {
    setSelectedUser(user)
    setIsEditOpen(true)
  }
  const handleEditClose = () => {
    setIsEditOpen(false)
    setSelectedUser(null)
  }

  const columns = [
    columnHelper.accessor('username', {
      id: 'username',
      header: () => (
        <Text
          textAlign='center' 
          align='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Name
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          {' '}
          <Text color={textColor} fontSize='sm' fontWeight='700'>
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('email', {
      id: 'email',
      header: () => (
        <Text
          textAlign='center' 
          align='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Email
        </Text>
      ),
      cell: info => (
        <Flex justifyContent='center' alignItems='center'>
          {' '}
          {/* ADDED WRAPPER */}
          <Text color={textColor} fontSize='sm' fontWeight='700'>
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor('role', {
      id: 'role',
      header: () => (
        <Text
          textAlign='center'
          align='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Role
        </Text>
      ),
      cell: info => {
        const role = info.getValue() || 'admin'
        const isGateKeeper = role === 'gate_keeper'
        return (
          <Flex justifyContent='center' alignItems='center'>
            <Badge
              colorScheme={isGateKeeper ? 'teal' : 'purple'}
              fontSize='0.8em'
              px='2.5'
              py='0.5'
              borderRadius='md'
              textTransform='capitalize'
            >
              {isGateKeeper ? 'Gate Keeper' : 'Admin'}
            </Badge>
          </Flex>
        )
      },
    }),
    columnHelper.accessor('created_by', {
      id: 'created_by',
      header: () => (
        <Text
          // REMOVED: justifyContent='space-between'
          textAlign='center' // ADDED
          align='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Created By
        </Text>
      ),
      cell: info => {
        const createdBy = info.getValue()
        const username =
          typeof createdBy === 'object' && createdBy !== null
            ? createdBy.username
            : createdBy
            ? `Admin #${createdBy}`
            : 'System'
        return (
          <Flex justifyContent='center' alignItems='center'>
            <Text color={textColor} fontSize='sm' fontWeight='700'>
              {username}
            </Text>
          </Flex>
        )
      },
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => (
        <Text
          // REMOVED: justifyContent='space-between'
          textAlign='center' // ADDED
          align='center'
          fontSize={{ sm: '10px', lg: '12px' }}
          color='gray.400'
        >
          Actions
        </Text>
      ),
      cell: ({ row }) => (
        <Flex justifyContent='center' alignItems='center'>
          {' '}
          {/* ADDED WRAPPER */}
          <ButtonGroup size='sm' isAttached>
            <Button
              colorScheme='brand'
              onClick={() => handleEditOpen(row.original)}
            >
              <EditIcon />
            </Button>
            <Button
              colorScheme='red'
              onClick={() => handleSingleDelete(row.original.id)}
            >
              <AiOutlineDelete />
            </Button>
          </ButtonGroup>
        </Flex>
      ),
      enableSorting: false,
    }),
  ]

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const onDelete = async () => {
    if (!selectedUserForDeletion) return

    mutate(
      { id: selectedUserForDeletion },
      {
        onSuccess: () => {
          onClose()
          setSelectedUserForDeletion(null)
          refetch()
          toast({
            title: 'User Deleted.',
            description: 'The user has been successfully deleted.',
            status: 'success',
            duration: 3000,
            isClosable: true,
            position: 'top',
          })
        },
        onError: () => {
          toast({
            title: 'Failed to Delete User.',
            description: 'There was an error deleting the user.',
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top',
          })
        },
      },
    )
  }

  if (isLoading) {
    return <SkeletonTable />
  }

  if (isError || !data) {
    return (
      <VStack spacing={4} align='center' mt='20'>
        <Alert status='error' variant='subtle'>
          <AlertIcon />
          <Box>
            <AlertTitle>Error Occurred!</AlertTitle>
            <AlertDescription>
              Unable to load user data. Please try again later.
            </AlertDescription>
          </Box>
        </Alert>
        <Button onClick={refetch} colorScheme='red' isLoading={isLoading}>
          Retry
        </Button>
      </VStack>
    )
  }

  return (
    <Card
      flexDirection='column'
      w='100%'
      px='0px'
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px='25px' mb='8px' justifyContent='space-between' align='center'>
        <Text
          color={textColor}
          fontSize='22px'
          fontWeight='700'
          lineHeight='100%'
        >
          Users
        </Text>
        <Button
          leftIcon={<AiOutlineUserAdd />}
          colorScheme='brand'
          size='sm'
          borderRadius='12px'
          onClick={onOpenCreateUser}
        >
          New User
        </Button>

        <DeleteUserModal
          isOpen={isOpen}
          onClose={onClose}
          onDelete={onDelete}
          isLoading={isPending}
          isDisabled={isPending}
          userId={selectedUserForDeletion}
        />

        <CreateUserModal isOpen={isCreateOpen} onClose={onCloseCreateUser} />

        <EditUserModal
          isOpen={isEditOpen}
          onClose={handleEditClose}
          user={selectedUser}
        />
      </Flex>

      <Box
        maxH='600px'
        overflowY='auto'
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#999 #ddd' }}
      >
        <Table color='gray.500' mb='24px' mt='12px'>
          <Thead position='sticky' zIndex='1' top='0' bg={TheadBackground}>
            {table.getHeaderGroups().map(headerGroup => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    borderColor={borderColor}
                    textAlign='center'
                  >
                    <Flex
                      justifyContent='center'
                      align='center'
                      fontSize={{ sm: '10px', lg: '12px' }}
                      color='gray.400'
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {header.column.getIsSorted() === 'asc'
                        ? '↑'
                        : header.column.getIsSorted() === 'desc'
                        ? '↓'
                        : null}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <Tbody>
            {table.getRowModel().rows.map(row => (
              <Tr
                key={row.id}
                _hover={{
                  bg: hoverBackgroundColor,
                }}
              >
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {data && (
        <CustomPagination
          total={data?.total || 0}
          current={pagination.current}
          pageSize={pagination.pageSize}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </Card>
  )
}
